import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Building2, Plus, Edit2, Trash2, Loader2, Check } from "lucide-react";

const tiposCentro = [
  { value: "producao", label: "Produção", icon: "🌾" },
  { value: "administrativo", label: "Administrativo", icon: "🏢" },
  { value: "manutencao", label: "Manutenção", icon: "🔧" },
  { value: "infraestrutura", label: "Infraestrutura", icon: "🏗️" },
  { value: "pecuaria", label: "Pecuária", icon: "🐄" },
  { value: "outros", label: "Outros", icon: "📦" }
];

export default function CentroCustoManager({ centrosCusto, propriedadeId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCentro, setEditingCentro] = useState(null);
  const [formData, setFormData] = useState({ nome: "", tipo: "producao", descricao: "" });
  
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data) => agrogestao.entities.CentroCusto.create({ ...data, propriedade_id: propriedadeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centros_custo'] });
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.CentroCusto.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['centros_custo'] });
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.CentroCusto.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['centros_custo'] })
  });

  const resetForm = () => {
    setFormData({ nome: "", tipo: "producao", descricao: "" });
    setEditingCentro(null);
    setDialogOpen(false);
  };

  const handleEdit = (centro) => {
    setFormData({ nome: centro.nome, tipo: centro.tipo, descricao: centro.descricao || "" });
    setEditingCentro(centro);
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCentro) {
      updateMutation.mutate({ id: editingCentro.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getTipoInfo = (tipo) => tiposCentro.find(t => t.value === tipo) || tiposCentro[5];

  return (
    <Card className="bg-white border-stone-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-indigo-600" />
          Centros de Custo
        </CardTitle>
        <Button size="sm" onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-1" />
          Novo
        </Button>
      </CardHeader>
      <CardContent>
        {centrosCusto.length === 0 ? (
          <p className="text-center text-stone-500 py-4">Nenhum centro de custo cadastrado</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {centrosCusto.map(centro => {
              const tipoInfo = getTipoInfo(centro.tipo);
              return (
                <div key={centro.id} className="p-3 bg-stone-50 rounded-lg border border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{tipoInfo.icon}</span>
                    <div>
                      <p className="font-medium text-stone-800">{centro.nome}</p>
                      <Badge variant="secondary" className="text-xs">{tipoInfo.label}</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(centro)}>
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteMutation.mutate(centro.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCentro ? "Editar Centro de Custo" : "Novo Centro de Custo"}</DialogTitle>
            <DialogDescription>
              {editingCentro ? "Atualize os dados do centro de custo" : "Cadastre um novo centro de custo para alocação de despesas"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: Lavoura Norte, Sede..."
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiposCentro.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.icon} {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                placeholder="Descrição opcional..."
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1">Cancelar</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />{editingCentro ? "Atualizar" : "Criar"}</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export { tiposCentro };
